import { shortReference } from "../../../shared/references/shortReferences";
import { readCheckRun, type CheckResult, type CheckRun, type CheckStatus } from "../../../api/content/reviewDecoders";
import { isJsonObject, JsonContractError } from "../../../api/http/jsonDecoding";
import type { ToolValueOutput, TurnUsage } from "../../../api/threads/liveMessages";
import { appPaths } from "../../../shared/routing/appPaths";
import i18n from "../../../i18n";
import { parseContentAddress, SEGMENT_SEPARATOR } from "../../../shared/references/contentReferences";

/**
 * How a tool run's values read in the Thread (processes.md P-6, P-7): what each value kind
 * shows at a glance, and how long a run took. The full value stays one click away as JSON,
 * so nothing a tool produced is hidden.
 */

export type ValueKind = ToolValueOutput["value_kind"];

/** At most this many units are listed; the rest are counted. */
export const PREVIEW_ITEMS = 5;
/** At most this many ranked results are listed; the rest are counted. */
export const RANKED_PREVIEW_ITEMS = 10;

/**
 * One result of a ranking (`item_refs`, any search, match or selection a tool makes): its
 * reference, score, what to show, and where it leads in the app, if anywhere.
 */
export interface RankedResult {
  readonly reference: string;
  readonly score: number;
  readonly label: string | null;
  readonly snippet: string | null;
  readonly href: string | null;
}

export type ValuePreview =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "units"; readonly units: readonly { readonly reference: string; readonly text: string }[]; readonly more: number }
  | { readonly kind: "ranked"; readonly results: readonly RankedResult[]; readonly more: number }
  | { readonly kind: "item"; readonly reference: string | null; readonly itemKind: string | null }
  | { readonly kind: "checks"; readonly run: CheckRun; readonly changeSetId: string | null }
  | { readonly kind: "documents"; readonly count: number }
  | { readonly kind: "none" };

/** A short view of a value; `none` when only the JSON says it well. */
export function valuePreview(kind: ValueKind, value: unknown): ValuePreview {
  if (kind === "source_version" && isJsonObject(value) && value["format"] === "dmw-source-version/1" && typeof value["source"] === "string" && typeof value["commit"] === "string" && typeof value["selected_count"] === "number" && typeof value["selected_bytes"] === "number") {
    return { kind: "text", text: i18n.t("threads.toolTurn.sourceVersionSummary", { source: value["source"], commit: shortReference("gitObject", value["commit"]), files: i18n.t("threads.toolTurn.fileCount", { count: value["selected_count"] }), bytes: i18n.t("threads.toolTurn.byteCount", { count: value["selected_bytes"] }) }) };
  }
  if ((kind === "text" || kind === "choice") && typeof value === "string") {
    return { kind: "text", text: value };
  }
  if (kind === "text_units" && Array.isArray(value)) {
    const units = value.flatMap((unit) =>
      isJsonObject(unit) && typeof unit["unit"] === "string" && typeof unit["text"] === "string" ? [{ reference: unit["unit"], text: unit["text"] }] : [],
    );
    return { kind: "units", units: units.slice(0, PREVIEW_ITEMS), more: Math.max(0, units.length - PREVIEW_ITEMS) };
  }
  if (kind === "item_refs" && Array.isArray(value)) {
    const entries = value.filter(isRankedEntry);
    return { kind: "ranked", results: entries.slice(0, RANKED_PREVIEW_ITEMS).map(rankedResult), more: Math.max(0, entries.length - RANKED_PREVIEW_ITEMS) };
  }
  if (kind === "item" && isJsonObject(value)) {
    const reference = value["ref"];
    const itemKind = value["kind"];
    return { kind: "item", reference: typeof reference === "string" ? reference : null, itemKind: typeof itemKind === "string" ? itemKind : null };
  }
  if (kind === "check_report") {
    return checksPreview(value);
  }
  if (kind === "workspace_file" && isJsonObject(value) && typeof value["path"] === "string") {
    return { kind: "text", text: value["path"] };
  }
  if (kind === "html_page" && isJsonObject(value) && typeof value["title"] === "string") {
    return { kind: "text", text: value["title"] };
  }
  if (kind === "corpus_import" && isJsonObject(value) && typeof value["document_count"] === "number") {
    return { kind: "documents", count: value["document_count"] };
  }
  return { kind: "none" };
}

interface RankedEntry {
  readonly [field: string]: unknown;
  readonly ref: string;
  readonly score: number;
}

function isRankedEntry(entry: unknown): entry is RankedEntry {
  return isJsonObject(entry) && typeof entry["ref"] === "string" && typeof entry["score"] === "number";
}

function rankedResult(entry: RankedEntry): RankedResult {
  const text = (field: string) => (typeof entry[field] === "string" ? entry[field] : null);
  return { reference: entry.ref, score: entry.score, label: text("label"), snippet: text("snippet"), href: rankedHref(entry.ref, text("refers_to")) };
}

/**
 * Where a ranked reference leads: a document or segment to its source page, an item to its
 * item page. A term has no page without an ontology version, so it stays text. Without
 * `refers_to` the form decides: `scheme:` starts an IRI, otherwise `#` names a segment and
 * anything else a document version.
 */
function rankedHref(reference: string, refersTo: string | null): string | null {
  const target = refersTo ?? (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(reference) ? "term" : reference.includes(SEGMENT_SEPARATOR) ? "segment" : "document");
  if (target === "item") {
    return appPaths.item(reference);
  }
  if (target !== "document" && target !== "segment") {
    return null;
  }
  const address = parseContentAddress(reference);
  return address === null ? null : appPaths.sourceDocument(address.document, address.segmentPath);
}

/** A check report reads like the review's checks; one that does not fit stays JSON. */
function checksPreview(value: unknown): ValuePreview {
  try {
    const changeSetId = isJsonObject(value) ? value["change_set_id"] : null;
    return { kind: "checks", run: readCheckRun(value), changeSetId: typeof changeSetId === "string" ? changeSetId : null };
  } catch (error) {
    if (error instanceof JsonContractError) {
      return { kind: "none" };
    }
    throw error;
  }
}

/** Whether a turn has usage to show: it called a model, or its cost is known. */
export function hasUsage(usage: TurnUsage): boolean {
  return usage.records.length > 0 || usage.total.cost !== null;
}

/** Milliseconds between two ISO 8601 times, or `null` if either is missing or unreadable. */
export function runDurationMilliseconds(startedAt: string | null, finishedAt: string | null): number | null {
  if (startedAt === null || finishedAt === null) {
    return null;
  }
  const duration = Date.parse(finishedAt) - Date.parse(startedAt);
  return Number.isNaN(duration) || duration < 0 ? null : duration;
}

/** "850 ms", "1.1 s", "2 min 5 s", in the user's language. */
export function formatDuration(milliseconds: number, language: string | undefined): string {
  if (milliseconds < 1000) {
    return new Intl.NumberFormat(language, { style: "unit", unit: "millisecond", maximumFractionDigits: 0 }).format(milliseconds);
  }
  if (milliseconds < 60_000) {
    return new Intl.NumberFormat(language, { style: "unit", unit: "second", maximumFractionDigits: 1 }).format(milliseconds / 1000);
  }
  const minutes = Math.floor(milliseconds / 60_000);
  const seconds = Math.round((milliseconds % 60_000) / 1000);
  const unit = (value: number, name: "minute" | "second") => new Intl.NumberFormat(language, { style: "unit", unit: name, unitDisplay: "short" }).format(value);
  return seconds === 0 ? unit(minutes, "minute") : `${unit(minutes, "minute")} ${unit(seconds, "second")}`;
}

/** How many checks failed, could not run, passed and were not checked, in that order; kinds with none are left out. */
export function checkCounts(results: readonly CheckResult[]): { readonly status: CheckStatus; readonly count: number }[] {
  const order: readonly CheckStatus[] = ["fail", "cannot_run", "pass", "not_checked"];
  return order
    .map((status) => ({ status, count: results.filter((result) => result.status === status).length }))
    .filter(({ count }) => count > 0);
}
